// Casa as fases de recompensa (calculadas por recompensas.js) com o conteúdo
// da região. Função pura, sem React e sem conhecer o AVA.
//
// Duas regras diferentes:
// - RAROS: casados por ordem, mas ALINHADOS PELO FIM. A ÚLTIMA fase rara recebe o
//   ÚLTIMO card do arco, a penúltima o penúltimo, e assim para trás. É o que
//   preserva o arco narrativo do mascote (ameaça → ajuda) para qualquer
//   quantidade de itens, e o que garante que a trilha SEMPRE termine no fecho.
//   Desde 24/08/2026 a CARTA rara é sorteada; o alinhamento continua valendo,
//   porque é ele que diz QUAL momento do arco é aquela fase — com carta ou sem.
// - COMUNS: vêm de um sorteio feito uma vez por trilha e guardado (ver
//   ava/progressoDoAluno.js). Aqui só se resolve o id sorteado contra o banco.
//
// Alinhar pelo fim e não pelo começo importa quando há UMA fase rara só — o caso
// da trilha de 2 a 4 passos, que tem uma leva e portanto um único raro. Casado
// pelo começo, o aluno recebia "A Caatinga em Perigo" ("meu lar está
// desaparecendo") como carta FINAL da trilha, sem confete, e a trilha terminava
// no meio do arco: no tom triste da ameaça, sem a resposta que o mascote dá em
// "Vamos Cuidar da Caatinga!". O fecho é o único card com `comemorar: true`, então
// o defeito também tirava o confete da cerimônia de encerramento.
//
// ---------------------------------------------------------------------------
// A CARTA RARA É SORTEADA, E O ARCO NÃO DEPENDE DELA (24/08/2026)
// ---------------------------------------------------------------------------
// Até aqui a rara era GARANTIDA: toda trilha entregava de 1 a 2, sempre. "Rara"
// que sai toda vez não é rara — em duas trilhas o aluno aprende que fim de leva dá
// carta grande, e a surpresa morre.
//
// Sortear sozinho quebraria duas coisas que a feature construiu com cuidado: o
// ARCO (ameaça → ajuda), que vivia DENTRO das cartas raras, e o FECHO da trilha,
// que é o motivo do alinhamento pelo fim. A solução foi separar a HISTÓRIA do
// PRÊMIO:
//
//   - o fim de leva SEMPRE acontece como cerimônia, e o mascote sempre conta a
//     sua parte do arco — triste na ameaça, feliz na ajuda;
//   - a CARTA é que é sorteada. Caiu, o aluno leva o colecionável; não caiu, ele
//     viveu a mesma cena e ouviu a mesma fala, só não leva a lembrança.
//
// Quem devolve isso é `cenaDaFase`, no fim deste arquivo: `{ card }` ou `{ arco }`.
// `recompensaDaFase` continua respondendo só sobre CARTA — é o que o álbum e a
// coleção do servidor precisam saber.
//
// O sorteio NÃO usa `Math.random()`: é derivado da mesma semente dos comuns, com
// sufixo próprio (ver ava/progressoDoAluno.js). Reprodutível por construção, então
// não precisa ser guardado nem migrado — o mesmo aluno, na mesma trilha, tem
// sempre o mesmo resultado em qualquer dispositivo.

import { distribuicaoDeRecompensas } from "./recompensas";

/**
 * A chance de CADA carta rara cair. Decisão do Kilson em 21/08/2026, tomada com a
 * conta na mão.
 *
 * São dois sorteios independentes (um por arco) numa trilha de 5 ou mais fases:
 *
 * | resultado                | chance |
 * |--------------------------|--------|
 * | termina com **0** raras  | 45%    |
 * | termina com 1            | 44%    |
 * | termina com as 2         | 11%    |
 *
 * Isso é aceito de olhos abertos: quase metade dos alunos termina a trilha vendo
 * as duas cenas do arco e com o álbum só de comuns. É o preço da raridade, e é
 * reversível — esta constante é o botão de ajuste, e nada mais precisa mudar.
 *
 * CASO-LIMITE que não pode ser esquecido: trilha de 2 a 4 fases tem uma leva só,
 * logo UM arco só (o de ajuda, que é o alinhado pelo fim). Ali é um sorteio, não
 * dois, e a chance de terminar sem rara é 67%. Eram 16 das 93 trilhas de
 * homologação em 21/08/2026.
 */
export const CHANCE_DE_CARTA_RARA = 1 / 3;

// Quanto do banco de uma região uma trilha pode levar de uma vez. Ver o teto em
// `QUANTAS_COMUNS`: é isto que impede a trilha longa de esgotar a coleção.
export const FRACAO_MAXIMA_DO_BANCO = 1 / 4;

/**
 * QUANTAS cartas comuns a trilha entrega. Decisão 5 do plano de 24/08/2026, e o
 * botão de ajuste de toda a frente.
 *
 * `ceil(√N) − 2`, limitada ao teto do banco. A tabela do estudo, simulada sobre as
 * 98 trilhas reais de homologação:
 *
 * | N (fases) | 3 | 4 | 6 | 8 | 10 | 12 | 16 | 22 | 30 | 39 | média |
 * |-----------|---|---|---|---|----|----|----|----|----|----|-------|
 * | antes     | 1 | 1 | 2 | 2 |  4 |  4 |  6 | 10 | 14 | 18 | 4,10  |
 * | **hoje**  | 0 | 0 | 1 | 1 |  2 |  2 |  2 |  3 |  4 |  5 | 1,55  |
 *
 * Por que SUBLINEAR e não `N/4` ou `N/6`: uma regra linear continua crescendo sem
 * limite e volta a comer o banco numa trilha de 60 fases, que o professor pode
 * montar amanhã. A raiz dá mais para a trilha longa — que é o pedido — sem que
 * "mais longa" signifique "leva o banco inteiro".
 *
 * O `− 2` é o que faz a trilha de até 4 fases não ganhar comum nenhuma (decisão 6,
 * 16% das trilhas de hoje): ali só existe a chance da rara, e é de propósito.
 *
 * O TETO É O BANCO, e não um número solto: nenhuma trilha leva mais de um quarto
 * das cartas da região. Com as 20 de hoje isso é 5; se um dia a região tiver 40, o
 * teto sobe sozinho para 10. É este número que decide a JORNADA da coleção —
 * medido, 43 trilhas para completar uma região de 20 (contra 14 na regra antiga).
 *
 * @param {number} totalFases
 * @param {number} tamanhoDoBanco  quantas comuns a região tem
 * @returns {number}
 */
export function QUANTAS_COMUNS(totalFases, tamanhoDoBanco = 0) {
  if (!totalFases || totalFases <= 0) return 0;
  const pedido = Math.max(0, Math.ceil(Math.sqrt(totalFases)) - 2);
  const teto = Math.floor(Math.max(0, tamanhoDoBanco) * FRACAO_MAXIMA_DO_BANCO);
  return Math.min(pedido, teto);
}

/**
 * Quanto de aleatório tem a posição da carta. Decisão 7 do plano.
 *
 * O sorteio parte as fases elegíveis em `k` blocos e tira UMA de cada. O jitter é a
 * largura da janela dentro do bloco, medida a partir do centro: `1` é o bloco
 * inteiro, `0` é sempre o centro (nada aleatório).
 *
 * Medido em 200 mil sorteios por linha, numa trilha de 39 fases com 5 cartas:
 *
 * | jitter | duas cartas em nós vizinhos | pior vão seco | posições possíveis |
 * |--------|------------------------------|---------------|--------------------|
 * | 1,0    | 10,4%                        | 11            | 37 de 37           |
 * | **0,7**| **0,0%**                     | 9             | **26 de 37**       |
 * | 0,5    | 0,0%                         | 7             | 18 de 37           |
 * | 0,0    | 0,0%                         | 5             | 6 — nada aleatório |
 *
 * 0,7 é o ponto: zera as cartas coladas em nós vizinhos (que leem como bug, e não
 * como sorte) e ainda deixa 26 das 37 posições possíveis, então a carta muda de
 * lugar de uma trilha para a outra.
 *
 * DE BRINDE, uma regra que não precisou existir: com 0,7 a FASE 1 nunca recebe
 * carta (medido: 0,0% em todo N), porque o centro do primeiro bloco já está adiante
 * dela. A geometria do bloco resolve o que seria um `if` a mais.
 *
 * O sorteio UNIFORME — embaralhar as elegíveis e tirar k — foi medido e descartado:
 * numa trilha de 10 fases com 2 cartas, em 43% dos casos as duas caíam na MESMA
 * leva (metade da trilha sem nada), e numa de 22 com 5, em 70% dos casos duas
 * ficavam em nós vizinhos. Estratificado zera as duas coisas por construção, e não
 * por sorte.
 */
export const JITTER_DA_POSICAO = 0.7;

/**
 * EM QUAIS fases as cartas comuns caem: sorteio estratificado com jitter.
 *
 * Separada de `sortearComuns` porque é a única parte que o teste precisa varrer em
 * todo N sem inventar um banco de cartas.
 *
 * @param {number}   totalFases
 * @param {number}   quantas    o `k`, já resolvido
 * @param {Function} aleatorio  injetável: em runtime é derivado da semente
 * @param {number}   [jitter]
 * @returns {number[]} fases (1-based), em ordem crescente, sem repetir
 */
export function posicoesDasComuns(
  totalFases,
  quantas,
  aleatorio = Math.random,
  jitter = JITTER_DA_POSICAO
) {
  const { elegiveis } = distribuicaoDeRecompensas(totalFases);
  if (quantas <= 0 || elegiveis.length === 0) return [];

  const escolhidas = [];
  for (let bloco = 0; bloco < quantas; bloco += 1) {
    const inicio = (bloco * elegiveis.length) / quantas;
    const fim = ((bloco + 1) * elegiveis.length) / quantas;
    const centro = (inicio + fim) / 2;
    const meiaJanela = ((fim - inicio) / 2) * jitter;

    // O `- 1e-9` tira o caso em que a borda superior cai exatamente num inteiro:
    // sem ele a janela invade o primeiro índice do bloco seguinte, e dois blocos
    // vizinhos podem escolher a mesma fase.
    //
    // Os dois `Math.min`/`Math.max` são cinto de segurança para blocos
    // degenerados (mais cartas que fases elegíveis, que `QUANTAS_COMUNS` não
    // produz): sem eles a janela pode virar vazia ou apontar para fora do array,
    // e o sorteio devolveria `undefined` como número de fase.
    const primeiro = Math.min(
      elegiveis.length - 1,
      Math.max(0, Math.ceil(centro - meiaJanela))
    );
    const ultimo = Math.min(
      elegiveis.length - 1,
      Math.max(primeiro, Math.floor(centro + meiaJanela - 1e-9))
    );

    const indice = primeiro + Math.floor(aleatorio() * (ultimo - primeiro + 1));
    escolhidas.push(elegiveis[Math.min(indice, ultimo)]);
  }

  return [...new Set(escolhidas)].sort((a, b) => a - b);
}

/**
 * Sorteia quais cards comuns caem em quais fases, sem repetir.
 *
 * Desde 24/08/2026 sorteia DUAS coisas, e por isso tem dois injetáveis: a POSIÇÃO
 * (quais fases recebem carta) e a ORDEM (qual carta do banco vai em cada uma). As
 * duas vêm de sementes com sufixos DIFERENTES em runtime — ver
 * ava/progressoDoAluno.js. Com uma semente só as duas ficariam correlacionadas, que
 * é a mesma lição que o sorteio das raras já tinha aprendido.
 *
 * O FORMATO GUARDADO NÃO MUDOU, e é o que faz esta frente não precisar de migração:
 * continua sendo `{ fase: cardId }`, no `localStorage` e na coluna `drawn_cards` do
 * servidor. Sortear a posição mudou QUAIS chaves o mapa tem, e nada mais. Quem está
 * no meio de uma trilha antiga mantém as cartas onde já as viu — o `drawn_cards` do
 * servidor tem precedência (ver ava/sincronia.js), e é por isso que `faseEntregaCarta`
 * pergunta ao MAPA e não à geometria.
 *
 * @param {number}   totalFases
 * @param {Array}    cardsComuns  banco de cards da região
 * @param {Function} aleatorio    injetável: sorteia a POSIÇÃO
 * @param {Function} embaralhar   injetável: sorteia a ORDEM das cartas
 * @param {number}   [quantas]    só o override de QA passa outro valor (ver
 *   ava/visualizacao.js) — em runtime é `QUANTAS_COMUNS`
 * @returns {Object} mapa { [numeroDaFase]: idDoCard }
 */
export function sortearComuns(
  totalFases,
  cardsComuns = [],
  aleatorio = Math.random,
  embaralhar = embaralharAleatorio,
  quantas = null
) {
  const banco = cardsComuns || [];
  const k =
    quantas === null || quantas === undefined
      ? QUANTAS_COMUNS(totalFases, banco.length)
      : Math.max(0, quantas);

  const fases = posicoesDasComuns(totalFases, k, aleatorio);
  const sorteados = embaralhar(banco).slice(0, fases.length);

  const mapa = {};
  fases.forEach((fase, indice) => {
    const card = sorteados[indice];
    if (card) mapa[fase] = card.id;
  });
  return mapa;
}

/**
 * Sorteia em quais fases raras a CARTA cai. Uma moeda por fase rara.
 *
 * Devolve só as que caíram (`{ 5: true }`), e não um mapa de booleanos: quem lê
 * pergunta "esta fase deu carta?", e a ausência responde "não" sem precisar de
 * `=== false`. Um mapa esparso também é o que sobrevive melhor a ser guardado
 * algum dia (ver a nota sobre `drawn_cards` no plano).
 *
 * A moeda é jogada para TODA fase rara, na ordem crescente, inclusive quando a
 * chance é 0 ou 1 — a sequência de números consumidos não pode depender do
 * resultado, senão mudar a chance mudaria também QUAIS fases são sorteadas em vez
 * de só quantas.
 *
 * @param {number}   totalFases
 * @param {number}   chance     0 a 1; o padrão é a decisão de produto
 * @param {Function} aleatorio  injetável: em runtime é derivado da semente
 * @returns {Object} mapa { [numeroDaFase]: true } só com as que caíram
 */
export function sortearRaras(
  totalFases,
  chance = CHANCE_DE_CARTA_RARA,
  aleatorio = Math.random
) {
  const { raras } = distribuicaoDeRecompensas(totalFases);

  const mapa = {};
  raras.forEach(fase => {
    const moeda = aleatorio();
    if (moeda < chance) mapa[fase] = true;
  });
  return mapa;
}

function embaralharAleatorio(lista) {
  const copia = [...lista];
  for (let i = copia.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copia[i], copia[j]] = [copia[j], copia[i]];
  }
  return copia;
}

/**
 * A fase entrega CARTA ao aluno?
 *
 * Comum sempre entrega; rara só quando o sorteio a contemplou. Existe porque o
 * TABULEIRO precisa da resposta e não tem o conteúdo da região em mãos — é ele
 * que acende a estrela dourada no nó concluído, e a estrela seria mentira numa
 * fase rara que não deu carta.
 *
 * PERGUNTA AO MAPA, e não à geometria (24/08/2026). Antes era
 * `comuns.includes(fase)`, com as comuns vindas de `distribuicaoDeRecompensas` — o
 * que funcionava enquanto a posição era fixa. Desde que ela é sorteada, a geometria
 * só sabe onde a carta PODE cair, e responder por ela criaria o defeito silencioso
 * que esta frente mais precisava evitar: numa trilha JÁ EM ANDAMENTO, cujo sorteio
 * antigo está congelado no `drawn_cards` do servidor, o baú apareceria num nó que
 * não dá carta e a estrela dourada afirmaria conquista que não houve.
 *
 * É o mesmo conserto que esta função fez em 24/08 para as raras, agora do outro
 * lado. A fase rara é conferida PRIMEIRO: se um mapa corrompido puser uma comum num
 * fim de leva, quem manda é o arco.
 *
 * @param {number} fase
 * @param {number} totalFases
 * @param {object} sorteio  { comuns, raras } — ver `cenaDaFase`
 */
export function faseEntregaCarta(fase, totalFases, sorteio = {}) {
  const { raras } = distribuicaoDeRecompensas(totalFases);
  if (raras.includes(fase))
    return Boolean(sorteio.raras && sorteio.raras[fase]);
  return Boolean(sorteio.comuns && sorteio.comuns[fase]);
}

/**
 * Qual card do arco vai numa fase rara, contando de trás para frente: a última
 * fase rara recebe o último card, a penúltima o penúltimo.
 *
 * Com 2 fases raras e 2 cards o resultado é o mesmo do casamento pelo começo
 * (ameaça, ajuda) — a diferença aparece quando as quantidades divergem:
 *
 * | fases raras | cards | resultado          |
 * |---|---|---|
 * | 2 | 2 | ameaça, ajuda      |
 * | 1 | 2 | **ajuda** (o fecho) |
 * | 3 | 2 | ameaça, ameaça, ajuda |
 *
 * O `Math.max(0, ...)` é o que faz o caso de mais fases que cards repetir o
 * PRIMEIRO card em vez de estourar o índice: repetir a ameaça no começo do arco
 * mantém o fecho no lugar certo, que é o que não pode se perder. Devolver
 * `undefined` quebraria a cerimônia.
 */
function indiceDoCardRaro(indiceRaro, totalRaras, cardsRaros) {
  const desdeOFim = totalRaras - 1 - indiceRaro;
  return Math.max(0, cardsRaros.length - 1 - desdeOFim);
}

/**
 * Card entregue ao concluir uma fase, ou null se a fase não entrega carta.
 *
 * `null` numa fase RARA deixou de significar "esta fase não premia" em
 * 24/08/2026: significa "o sorteio não deu a carta". A fase continua premiando —
 * com a CENA do arco, que é o que `cenaDaFase` devolve. Quem quer saber se há
 * cerimônia pergunta a ela, nunca a esta função.
 *
 * @param {number} fase       número da fase (1-based)
 * @param {number} totalFases
 * @param {object} conteudo   { cardsComuns, cardsRaros }
 * @param {object} sorteio    { comuns: mapa fase → id, raras: mapa fase → true }
 */
export function recompensaDaFase(
  fase,
  totalFases,
  conteudo = {},
  sorteio = {}
) {
  const { raras } = distribuicaoDeRecompensas(totalFases);
  const cardsRaros = conteudo.cardsRaros || [];
  const cardsComuns = conteudo.cardsComuns || [];

  const indiceRaro = raras.indexOf(fase);
  if (indiceRaro !== -1) {
    // Sorteio ausente conta como "não caiu", e é de propósito: um chamador que
    // esqueça de passar `raras` fica sem cartas raras, nunca com todas. Errar
    // para o lado de não entregar é o que um sorteio pode fazer de menos grave —
    // o inverso daria ao aluno uma carta que o servidor não vai gravar.
    if (!(sorteio.raras && sorteio.raras[fase])) return null;
    return (
      cardsRaros[indiceDoCardRaro(indiceRaro, raras.length, cardsRaros)] ?? null
    );
  }

  // Sem guarda de geometria desde 24/08/2026: quem responde "esta fase premia?" é
  // o MAPA, pelo mesmo motivo de `faseEntregaCarta`. Um `comuns.includes(fase)`
  // aqui devolveria `null` para a carta que uma trilha antiga já entregou naquele
  // nó — e o álbum perderia carta que o aluno tem.
  const id = (sorteio.comuns || {})[fase];
  if (!id) return null;
  return cardsComuns.find(card => card.id === id) ?? null;
}

/**
 * O que acontece ao concluir uma fase: `{ card }`, `{ arco }` ou `null`.
 *
 * É a pergunta que o host faz — "abro cerimônia, e com o quê?". As três respostas:
 *
 * | fase | sorteio | devolve |
 * |---|---|---|
 * | comum premiada | tem id | `{ card }` |
 * | rara | deu carta | `{ card }` |
 * | rara | não deu | `{ arco }` — a cena, sem prêmio |
 * | qualquer outra | — | `null` |
 *
 * O ARCO É A CARTA RARA SEM O OBJETO, e por isso não tem texto próprio: nome,
 * fala e `comemorar` saem do card que TERIA caído ali, pelo mesmo alinhamento
 * pelo fim. Foi a forma de cumprir "uma fonte só" (o plano pedia o texto no
 * descritor, o que duplicaria o que `gerar-cartas-trilha.py` já gera do MD) sem
 * tocar no gerador: o descritor acrescenta só o que a carta não tem, que é a
 * FIGURA — o mascote sem a moldura de item de coleção.
 *
 * @param {number} fase
 * @param {number} totalFases
 * @param {object} conteudo  { cardsComuns, cardsRaros, arco }
 * @param {object} sorteio   { comuns, raras }
 * @returns {{card: object}|{arco: object}|null}
 */
export function cenaDaFase(fase, totalFases, conteudo = {}, sorteio = {}) {
  const card = recompensaDaFase(fase, totalFases, conteudo, sorteio);
  if (card) return { card };

  const { raras } = distribuicaoDeRecompensas(totalFases);
  const indiceRaro = raras.indexOf(fase);
  if (indiceRaro === -1) return null;

  const arco = arcoDaFaseRara(indiceRaro, raras.length, conteudo);
  return arco ? { arco } : null;
}

// A cena do arco daquela fase rara. `null` quando falta o card ou a figura —
// região incompleta não pode abrir uma cerimônia vazia, e quem cobra a falta é
// `conteudo/contrato.js`.
//
// `pilula` e não `fala` no objeto devolvido, embora "fala" fosse o nome honesto:
// é o mesmo campo que a carta usa, e com o nome igual `CerimoniaCard` trata os
// dois assuntos pelo mesmo caminho em vez de ter dois ramos para o mesmo texto.
function arcoDaFaseRara(indiceRaro, totalRaras, conteudo) {
  const cardsRaros = conteudo.cardsRaros || [];
  const figuras = conteudo.arco || [];

  const indice = indiceDoCardRaro(indiceRaro, totalRaras, cardsRaros);
  const card = cardsRaros[indice];
  const figura = figuras[indice];
  if (!card || !figura) return null;

  return {
    figura,
    nome: card.nome,
    pilula: card.pilula,
    comemorar: Boolean(card.comemorar)
  };
}
